import {
  getItemOrDefaultToValue,
  getPersonName,
  nestedDisplay,
  transformArrayDisplay,
} from 'utils/details';
import { displayTime, systemTimezone } from '../../time';
import { images } from 'utils/icons';
import { mapMediaTypeKey } from '../Sites/SiteDetails';
import { typeNameConstructor } from './AssetList';
import assetCategories from 'static/asset_categories.json';
import { ruleColumns } from 'containers/Pages/Assets/Assets/columns';
import { setSiteName } from 'utils/helper/site';
import { LIST_WIDTH } from 'utils/constants/listview';
import { ASSET_STATUS } from 'containers/Pages/Assets/Assets/helper';
import { BASE_ASSET_CATEGORY } from 'utils/constants/assetText';

const getSiteNameFromSite = site => {
  if (!site || !site.name) return '-';
  else return site.name;
};

const transformAssignedSiteName = site => {
  return { ...site, label: setSiteName(site) };
};
const transformStatusAudit = auditList => {
  return auditList.map(a => {
    return {
      status: a.status,
      timestamp: displayTime({
        timestamp: getItemOrDefaultToValue(a, 'timestamp', '-'),
        systemTimezone,
      }),
    };
  });
};
export const transform = (data, globalDepartmentList) => {
  const selectedDepartments = globalDepartmentList.filter(f =>
    getItemOrDefaultToValue(data, 'departmentIds', []).includes(f.id),
  );
  const categoryIcon = assetCategories.find(f => f.id === data?.type?.category);
  const assetType = getItemOrDefaultToValue(data, 'type', '-');
  const { category } = data.type;
  return {
    generalInfo: {
      assetId: {
        label:
          category === BASE_ASSET_CATEGORY.VEHICLE.id
            ? 'Registration'
            : 'Asset ID',
        type: 'text',
        value: getItemOrDefaultToValue(data, 'assetId', '-'),
      },
      assetName: {
        type: 'text',
        value: getItemOrDefaultToValue(data, 'name', '-'),
      },
      category: {
        type: 'icons',
        value: categoryIcon.icon,
        label: category,
      },
      type: {
        type: 'text',
        customLabelValue: typeNameConstructor(
          data.type.name,
          data.type.assetTypeId,
        ),
        value: {
          typeName: getItemOrDefaultToValue(data.type, 'name', '-'),
          id: getItemOrDefaultToValue(data.type, 'assetTypeId', '-'),
        },
      },
      status: {
        type: 'icon-with-label',
        value: ASSET_STATUS[data.currentStatus],
      },
      assignedSite: {
        type: 'text',
        value:
          data.assignedSite !== null
            ? getSiteNameFromSite(data.assignedSite)
            : '-',
      },
      typeId: {
        type: 'none',
        hideLabel: true,
        value: getItemOrDefaultToValue(data.type, 'id', '-'),
      },
      departments: {
        type: 'array',
        value: nestedDisplay(selectedDepartments),
      },
    },
    detailedInfo: {
      qualificationRequired: {
        type: 'boolean',
        value: getItemOrDefaultToValue(data.type, 'qualificationRequired', '-'),
      },
      licenseRequired: {
        type: 'boolean',
        value: getItemOrDefaultToValue(data, 'licenseRequired', '-'),
      },
      handOverRequired: {
        type: 'boolean',
        value: getItemOrDefaultToValue(data.type, 'handoverRequired', '-'),
      },
      description: {
        type: 'text',
        value: getItemOrDefaultToValue(data, 'description', '-'),
      },
      surfaces: {
        type: 'detailImageTable',
        label: 'Images',
        value: mapMediaTypeKey(getItemOrDefaultToValue(data, 'surfaces', [])),
      },
      statusAudit: {
        type: 'list-data',
        label: 'Status History',
        value: {
          data: transformStatusAudit(data.statusAudit),
          columns: [
            {
              headerName: 'Status',
              cellRenderer: 'statusComponent',
              field: 'status',
              width: 100,
            },
            {
              headerName: 'Time',
              field: 'timestamp',
              width: LIST_WIDTH.TIME_DATE,
            },
          ],
        },
      },
    },
    others: {
      site: {
        value:
          data.assignedSite !== null
            ? transformAssignedSiteName(data.assignedSite)
            : '-',
      },
      image: {
        value:
          data.imageUrl !== null && data.imageUrl !== ''
            ? getItemOrDefaultToValue(data, 'imageUrl')
            : data.surfaces && data.surfaces.length > 0
            ? data.surfaces[0].imageUrl // apply first image as thumbnail
            : images.emptyEstablishment.default,
      },
      id: { value: getItemOrDefaultToValue(data, 'id') },
      archived: {
        value: getItemOrDefaultToValue(data, 'archived', false),
      },
      currentAssignee: {
        value:
          data.currentAssignee !== null
            ? getPersonName(data.currentAssignee.general)
            : '-',
      },
      category: {
        type: 'text',
        value: data.type.category,
      },
      type: {
        type: 'text',
        value: {
          ...assetType,
          typeName: assetType.name,
        },
      },
    },
  };
};

export const transformPhoneDetails = (data, globalDepartmentList) => {
  const details = transform(data, globalDepartmentList);
  return {
    generalInfo: {
      ...details.generalInfo,
      phone: {
        type: 'text',
        value:
          data.phoneNumber !== null
            ? `${getItemOrDefaultToValue(
                data.phoneNumber,
                'prefix',
              )} ${getItemOrDefaultToValue(data.phoneNumber, 'number', '-')}`
            : '-',
      },
    },
    detailedInfo: {
      ...details.detailedInfo,
      phoneRegistration: {
        type: 'phone-registration',
        label: 'phone_registration_title',
        value: getItemOrDefaultToValue(data.currentStatus, 'status', '-'),
        id: getItemOrDefaultToValue(data, 'id'),
      },
      registerDate: {
        label: 'date_registered_title',
        type: 'text',
        value:
          data.registered !== null
            ? displayTime({
                timestamp: getItemOrDefaultToValue(data, 'registered'),
                systemTimezone,
              })
            : '-',
      },
    },
    others: {
      ...details.others,
      phone: {
        value:
          data.phoneNumber !== null
            ? getItemOrDefaultToValue(data.phoneNumber, 'number', '-')
            : '-',
      },
      country: {
        value: data?.phoneNumber?.country,
      },
    },
  };
};
export const transformSensorDetails = (data, globalDepartmentList) => {
  const details = transform(data, globalDepartmentList);
  return {
    generalInfo: {
      ...details.generalInfo,
    },
    detailedInfo: {
      ...details.detailedInfo,
      rules: {
        type: 'list-data',
        value: {
          data: data.rules,
          columns: ruleColumns,
        },
      },
    },
    others: {
      ...details.others,
    },
  };
};
export const transformSensorForm = (data, globalDepartmentList) => {
  const details = transform(data, globalDepartmentList);
  return {
    generalInfo: {
      ...details.generalInfo,
    },
    detailedInfo: {
      ...details.detailedInfo,
      rules: {
        type: 'array',
        value: transformArrayDisplay(data.rules, [], false),
      },
    },
    others: {
      ...details.others,
    },
  };
};
